import { CustomerTypes, PersonalInfoFormActionType } from "app/hooks/";
import { useCheckoutInfoContext } from "pages/checkout/providers/CheckoutInfoProvider";
import { useInfoFormErrorsContext } from "pages/checkout/providers/InfoFormErrorsProvider";
import { useEffect } from "react";
import { api } from "shared/api";
import FormGroup from "shared/ui/FormGroup";
import Checkbox from "shared/ui/checkbox";
import Input from "shared/ui/input";
import Label from "shared/ui/label";
import Phone from "shared/ui/phone";
import Heading from "shared/ui/typography/Heading";
import { concatClassnames } from "shared/utils/concatClassnames";

interface CheckoutInfoDeliveryProps {
  className?: string;
  isBusiness?: boolean;
}

const CheckoutInfoDelivery = ({
  className,
  isBusiness,
}: CheckoutInfoDeliveryProps) => {
  const { state, dispatch } = useCheckoutInfoContext();
  const { formErrors } = useInfoFormErrorsContext();

  const {
    zipCode,
    houseNumber,
    municipality,
    street,
    city,
    province,
    isSameAddress,
    shippingPhone,
  } = state;

  const updatePostcode = (e: any) => {
    dispatch({
      type: PersonalInfoFormActionType.UPDATE_ZIP_CODE,
      payload: e.target.value,
    });
  };
  const updateHouseNo = (e: any) => {
    dispatch({
      type: PersonalInfoFormActionType.UPDATE_HOUSE_NUMBER,
      payload: e.target.value,
    });
  };

  const updateShippingPhone = (phone: string) => {
    dispatch({
      type: PersonalInfoFormActionType.UPDATE_SHIPPING_PHONE,
      payload: phone,
    });
  };
  const updateIsSameAddress = (e: any) => {
    dispatch({
      type: PersonalInfoFormActionType.UPDATE_IS_SAME_ADDRESS,
      payload: !isSameAddress,
    });
  };

  const verifyZipCode = async () => {
    if (!houseNumber) return;
    if (!zipCode) return;

    try {
      const res = await api().gersValidateZipCode(zipCode, houseNumber);

      dispatch({
        type: PersonalInfoFormActionType.UPDATE_STREET,
        payload: res.data.street,
      });
      dispatch({
        type: PersonalInfoFormActionType.UPDATE_CITY,
        payload: res.data.city,
      });
      dispatch({
        type: PersonalInfoFormActionType.UPDATE_MUNICIPALITY,
        payload: res.data.municipality,
      });
      dispatch({
        type: PersonalInfoFormActionType.UPDATE_PROVINCE,
        payload: res.data.province,
      });
    } catch (err: any) {
      // setIsAddressVerified(false);
      if (street) {
        dispatch({
          type: PersonalInfoFormActionType.UPDATE_STREET,
          payload: "",
        });
      }

      if (city) {
        dispatch({
          type: PersonalInfoFormActionType.UPDATE_CITY,
          payload: "",
        });
      }
      if (municipality) {
        dispatch({
          type: PersonalInfoFormActionType.UPDATE_MUNICIPALITY,
          payload: "",
        });
      }
      if (province) {
        dispatch({
          type: PersonalInfoFormActionType.UPDATE_PROVINCE,
          payload: "",
        });
      }
    }
  };

  useEffect(() => {
    if (!zipCode) return;
    if (!houseNumber) return;

    verifyZipCode().catch(console.error);
  }, [zipCode, houseNumber]);

  return (
    <div className={concatClassnames("gg-grid gg-grid-cols-12", className)}>
      <div className="gg-col-span-12 gg-mb-8 gg-grid gg-grid-cols-12 gg-gap-5">
        <Heading
          headingLevel="h2"
          className="gg-col-span-12 gg-mb-2 gg-text-2xl"
        >
          Bezorgadres
        </Heading>

        <FormGroup
          className="sm:gg-col-span-6"
          errorMessage={formErrors["zipCode"]}
          displayError={state.isSubmitting}
        >
          <Label htmlFor="zipCode" className="gg-mb-1" required>
            Postcode
          </Label>
          <Input
            id="zipCode"
            placeholder="Postcode"
            value={zipCode}
            onChange={updatePostcode}
            error={!!formErrors["zipCode"]}
            required
            data-testid="form-delivery-zip"
          />
        </FormGroup>

        <FormGroup
          className="sm:gg-col-span-6"
          errorMessage={formErrors["houseNumber"]}
          displayError={state.isSubmitting}
        >
          <Label htmlFor="houseNumber" className="gg-mb-1" required>
            Huisnummer
          </Label>
          <Input
            id="houseNumber"
            placeholder="Huisnummer"
            value={houseNumber}
            onChange={updateHouseNo}
            error={!!formErrors["houseNumber"]}
            required
            data-testid="form-delivery-house"
          />
        </FormGroup>

        <FormGroup
          className="sm:gg-col-span-6"
          errorMessage={formErrors["street"]}
          displayError={state.isSubmitting}
        >
          <Label htmlFor="adres" className="gg-mb-1" required>
            Adres
          </Label>
          <Input
            onChange={() => {}}
            value={street}
            id="adres"
            placeholder="Adres"
            required
            readOnly
          />
        </FormGroup>

        <FormGroup
          className="sm:gg-col-span-6"
          errorMessage={formErrors["city"]}
          displayError={state.isSubmitting}
        >
          <Label htmlFor="woonplaats" className="gg-mb-1" required>
            Woonplaats
          </Label>
          <Input
            onChange={() => {}}
            id="woonplaats"
            placeholder="Woonplaats"
            required
            value={city}
            readOnly
          />
        </FormGroup>

        {state.type === CustomerTypes.BUSINESS && (
          <FormGroup
            className="gg-col-span-12"
            errorMessage={formErrors["shippingPhone"]}
            displayError={state.isSubmitting}
          >
            <Label htmlFor="shippingPhone" className="gg-mb-1">
              Telefoonnummer
            </Label>
            <Phone
              id="shippingPhone"
              value={shippingPhone}
              error={!!formErrors["shippingPhone"]}
              onChange={(phone: string) => {
                updateShippingPhone(phone);
              }}
            />
          </FormGroup>
        )}
      </div>

      {!isBusiness && (
        <FormGroup className="gg-col-span-12 gg-flex" isInline>
          <Checkbox
            id="same-address"
            checked={isSameAddress}
            onChange={updateIsSameAddress}
          />

          <Label htmlFor="same-address">
            Factuuradres is gelijk aan het bezorgadres
          </Label>
        </FormGroup>
      )}
    </div>
  );
};
export default CheckoutInfoDelivery;
